import { forwardRef } from "react";
import { Fish, Anchor, MapPin, ChevronRight } from "lucide-react";

const categoryColors = {
  boat_ramp: 'bg-blue-500',
  trailhead: 'bg-green-500',
  bridge: 'bg-orange-500',
  parking: 'bg-purple-500',
  campground: 'bg-red-500',
  fishing_access: 'bg-orange-500',
};

export const HeroSearchDropdown = forwardRef(function HeroSearchDropdown(
  { results, selectedIndex, handleSelectLocation, query, isLoading },
  ref
) {
  const hasResults = results && (
    (results.destinations?.length > 0) ||
    (results.access_points?.length > 0) ||
    (results.locations?.length > 0)
  );

  let currentIndex = 0;

  if (!hasResults && !(query.length >= 2 && !isLoading)) return null;

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-border overflow-hidden z-[100] max-h-[60vh] overflow-y-auto"
      data-testid="search-dropdown"
    >
      {hasResults ? (
        <div className="py-2">
          {results.destinations?.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">Destinations</div>
              {results.destinations.map((dest) => {
                const itemIndex = currentIndex++;
                return (
                  <button
                    key={`dest-${dest.id}`}
                    onClick={() => handleSelectLocation({ latitude: dest.latitude, longitude: dest.longitude, type: "destination", data: dest })}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${selectedIndex === itemIndex ? 'bg-muted' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><Fish className="w-5 h-5 text-primary" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{dest.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{dest.state}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}

          {results.access_points?.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">Access Points</div>
              {results.access_points.map((point) => {
                const itemIndex = currentIndex++;
                return (
                  <button
                    key={`ap-${point.id}`}
                    onClick={() => handleSelectLocation({ latitude: point.latitude, longitude: point.longitude, type: "access_point", data: point })}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${selectedIndex === itemIndex ? 'bg-muted' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full ${categoryColors[point.category] || 'bg-orange-500'} flex items-center justify-center flex-shrink-0`}><Anchor className="w-5 h-5 text-white" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{point.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{point.county && `${point.county}, `}{point.state}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}

          {results.locations?.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">Locations</div>
              {results.locations.map((loc, idx) => {
                const itemIndex = currentIndex++;
                return (
                  <button
                    key={`loc-${idx}`}
                    onClick={() => handleSelectLocation({ latitude: loc.latitude, longitude: loc.longitude, type: "location", data: loc })}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${selectedIndex === itemIndex ? 'bg-muted' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-blue-600" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{loc.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{loc.full_name}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-muted-foreground">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No results found for &quot;{query}&quot;</p>
          <p className="text-sm mt-1">Try searching for a river, lake, or city name</p>
        </div>
      )}
    </div>
  );
});
